import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {Animated, AppState, Platform, Pressable, StatusBar, StyleSheet, Text, View} from "react-native";
import {useReferenceWindowDimensions} from "../utils/referenceViewport";
import {playerFont} from "../theme/tokens";
import {SafeAreaView, useSafeAreaInsets} from "react-native-safe-area-context";
import {NativeStackScreenProps} from "@react-navigation/native-stack";
import {RootStackParamList} from "../navigation/types";
import {
  getItem,
  JellyfinItem,
  imageSource,
  isPrewarmLeaseUsable,
  reportPlaybackTelemetry,
} from "../api/jellyfin";
import {useSession} from "../session/SessionProvider";
import {PlayerVideoSurface} from "../components/PlayerVideoSurface";
import {PlayerVideoHandle} from "../components/playerVideoTypes";
import {PlayerKeyboardControls} from "../components/PlayerKeyboardControls";
import {useMusicPlayback} from "../music/MusicPlaybackProvider";
import {getPlaybackSourceLabel} from "../utils/playbackSourcePresentation";
import {defaultPreferences, MediaSegmentType, readPreferences, savePreferences} from "../storage/preferences";
import Svg, { Defs, LinearGradient, Rect, Stop } from "react-native-svg";
import {isTelevisionRuntime} from "../utils/platformCapabilities";
import {detectWebHdrCapabilities, formatHdrSourceLabel} from "../player/hdrCapabilities";
import {usePlayerAppState} from "../player/usePlayerAppState";
import {usePlayerOrientationLock} from "../player/usePlayerOrientationLock";
import {usePlayerControlsVisibility} from "../player/usePlayerControlsVisibility";
import {usePlayerPulse} from "../player/usePlayerPulse";
import {usePlayerStartupWatchdog} from "../player/usePlayerStartupWatchdog";
import {usePlayerTrickplay} from "../player/usePlayerTrickplay";
import {usePlayerController} from "../player/usePlayerController";
import {usePlayerRuntimeAdapters} from "../player/usePlayerRuntimeAdapters";
import {PlayerBufferingMark, PlayerButton, PlayerPlaybackPulse, PlayerRecoveryNotice} from "../components/player/PlayerPrimitives";
import {PlayerPausedOverlay} from "../components/player/PlayerPausedOverlay";
import {PlayerFailurePanel, PlayerNextEpisodePrompt, PlayerPreparingSurface, PlayerSkipActions} from "../components/player/PlayerTransientSurfaces";
import {PlayerTopBar} from "../components/player/PlayerTopBar";
import {PlayerTimeline} from "../components/player/PlayerTimeline";
import {PlayerTransportControls} from "../components/player/PlayerTransportControls";
import {PlayerMenuControls, PlayerMenuKind} from "../components/player/PlayerMenuControls";
import {PlayerMenuShell} from "../components/player/PlayerMenuShell";
import {PlayerOptionsMenu} from "../components/player/PlayerOptionsMenu";
import {PlayerDiagnosticsMenu} from "../components/player/PlayerDiagnosticsMenu";
import {PlayerLanguageMenu} from "../components/player/PlayerLanguageMenu";
import {PlayerVersionsMenu} from "../components/player/PlayerVersionsMenu";
import {PlayerTrackMenu} from "../components/player/PlayerTrackMenu";
import {PlayerEpisodesMenu} from "../components/player/PlayerEpisodesMenu";
import {DiagnosticsSection, LanguageMenuEntry, buildDiagnosticsMetrics, buildLanguageMenuEntries, buildPlaybackStats} from "../player/presentation";
import { buildSubtitleTracks } from "../player/subtitleSource";
import {
  emptySourcePreference,
  getPlaybackSourceFingerprint,
  rankPlaybackSources,
} from "../player/sourceRanking";
import {
  getHlsSegmentLength,
  getHlsStartupVariant,
} from "../player/hlsStartupExperiment";
import { useQueryClient } from "@tanstack/react-query";
import {
  prefetchPlaybackInfoForItem,
} from "../player/playbackPrefetch";
import {usePlayerTVRemoteControls} from "../player/usePlayerTVRemoteControls";
import {usePlayerFullscreen} from "../player/usePlayerFullscreen";
import {shouldHoldPlayerControlsForFocus} from "../player/playerFocusPolicy";
import {focusRegisteredNode, setAppActivateHandler, setAppBackHandler, subscribeFocusTransitions} from "../tvNavigation/registry";

type Props = NativeStackScreenProps<RootStackParamList, "Player">;

export function PlayerScreen({ navigation, route }: Props) {
  usePlayerOrientationLock();
  const { width, height } = useReferenceWindowDimensions();
  const safeArea = useSafeAreaInsets();
  const compact = width < 700;
  const portraitCompact = compact && height > width;
  const shortLandscape = height < 540 && width > height;
  const televisionRuntime = isTelevisionRuntime();
  const { credentials } = useSession();
  const queryClient = useQueryClient();
  const musicPlayback = useMusicPlayback();
  useEffect(() => {
    musicPlayback.dismiss();
  }, []);
  const videoRef = useRef<PlayerVideoHandle>(null);
  const presentFullscreen = useCallback(() => videoRef.current?.presentFullscreen(), []);
  const dismissFullscreen = useCallback(() => videoRef.current?.dismissFullscreen(), []);
  const { fullscreenActive, toggleFullscreen } = usePlayerFullscreen(
    presentFullscreen,
    dismissFullscreen,
  );
  const pictureInPictureRequestedRef = useRef(false);
  const [pictureInPicturePresentation, setPictureInPicturePresentation] = useState(false);
  const startupStartedAtRef = useRef(Date.now());
  const telemetryItemRef = useRef(route.params.itemId);
  const telemetryEventsRef = useRef(new Set<string>());
  const automaticallySkippedSegmentsRef = useRef(new Set<string>());
  if (telemetryItemRef.current !== route.params.itemId) {
    telemetryItemRef.current = route.params.itemId;
    startupStartedAtRef.current = Date.now();
    telemetryEventsRef.current.clear();
  }
  const controller = usePlayerController(
    route.params.itemId,
    route.params.mediaSourceId,
  );
  const {
    snapshot: controllerSnapshot,
    engineState,
    sourcePort,
    dispatchLifecycle,
    setServerSeek,
    markProgressReported,
    shouldReportProgress,
    prepareTrackChange,
  } = controller;
  const {
    lifecycle,
    position,
    duration,
    buffered,
    paused,
    muted,
    volume,
    serverSeek,
    source: sourceController,
  } = controllerSnapshot;
  const {
    positionRef,
    pausedRef,
    mutedRef,
    playbackStartedRef,
  } = engineState;
  const {
    selectedSourceId,
    surfaceEpoch,
  } = sourceController;
  const [timelineFocused, setTimelineFocused] = useState(false);
  const [menu, setMenu] = useState<PlayerMenuKind>(null);
  const [diagnosticsSection, setDiagnosticsSection] = useState<DiagnosticsSection>("core");
  const [selectedSeasonId, setSelectedSeasonId] = useState("");
  const [preferences, setPreferences] = useState(defaultPreferences);
  const [preferencesReady, setPreferencesReady] = useState(false);
  const [nextPromptDismissed, setNextPromptDismissed] = useState(false);
  const [recoveryNotice, setRecoveryNotice] = useState("");
  const lastSurfacePressAt = useRef(0);
  const clickTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const {
    visible: controlsVisible,
    setVisible: setControlsVisible,
    focused: controlsFocused,
    setFocused: setControlsFocused,
    rendered: controlsRendered,
    opacity: controlsOpacity,
    noteActivity: noteControlsActivity,
  } = usePlayerControlsVisibility({
    paused,
    blocked: Boolean(menu),
    autoHideSeconds: preferences.playerAutoHideSeconds,
    reduceMotion: preferences.reduceMotion,
  });
  useEffect(() => setAppBackHandler(controlsVisible ? () => {
    setControlsVisible(false);
    setControlsFocused(false);
    return true;
  } : undefined), [controlsVisible, setControlsFocused, setControlsVisible]);
  useEffect(() => subscribeFocusTransitions((transition) => {
    if (transition.route !== "Player" || transition.modality === "pointer") return;
    setControlsVisible(true);
    noteControlsActivity();
  }), [noteControlsActivity, setControlsVisible]);
  const handleControlsFocusChange = useCallback((focused: boolean) => {
    setControlsFocused(focused && shouldHoldPlayerControlsForFocus());
  }, [setControlsFocused]);
  const {
    kind: pulse,
    opacity: pulseOpacity,
    scale: pulseScale,
    iconScale: pulseIconScale,
    show: showPlaybackPulse,
  } = usePlayerPulse(preferences.reduceMotion);

  useEffect(() => {
    void readPreferences().then((next) => {
      setPreferences(next);
      setPreferencesReady(true);
    });
  }, []);
  const {
    media: {
      itemQuery,
      playbackQuery,
      segmentsQuery,
      item,
      resumeSeconds,
      seasons,
      episodes,
      selectedSeason,
      previousEpisode,
      nextEpisode,
      seasonQuery,
      episodeQuery,
      sources,
      rankedSources,
      source,
      togglePinnedSource,
      toggleIgnoredSource,
      clearSourceScoring,
      managedHls,
      streamStartSeconds,
      streamUrl,
      deliveryBadge,
      streams: videoStreams,
      audioStreams,
      subtitleStreams,
      selectedAudioIndex,
      selectedSubtitleIndex,
      audioTrackIndex,
      subtitleTrackIndex,
      selectAudioTrack,
      selectSubtitleTrack,
    },
    timelineDuration,
    surfaceIdentity,
    decoderHandlers,
    actions: runtimeActions,
  } = usePlayerRuntimeAdapters({
    credentials,
    itemId: route.params.itemId,
    explicitSourceId: route.params.mediaSourceId,
    startOver: route.params.startOver,
    selectedSourceId,
    setSelectedSourceId: sourcePort.setSelectedSourceId,
    decoderReady: lifecycle.decoderReady,
    versionsRequested: menu === "versions",
    preferences,
    preferencesReady,
    setPreferences,
    selectedSeasonId,
    setSelectedSeasonId,
    allowAutomaticPinnedSelection: !playbackStartedRef.current,
    serverSeek,
    prepareTrackChange,
    lifecycleDuration: duration,
    lifecycleBuffered: buffered,
    lifecycleVolume: volume,
    positionRef,
    pausedRef,
    mutedRef,
    startedRef: playbackStartedRef,
    videoRef,
    dispatchLifecycle,
    markProgressReported,
    onSeekTimeout: () => setRecoveryNotice(
      "Seek could not be confirmed; restored the decoder position.",
    ),
    onActivity: () => setControlsVisible(true),
    bindSurface: sourcePort.bindSurface,
    sourcePort,
    sourceRecovering: sourceController.recovering,
    surfaceEpoch,
    setServerSeek,
    shouldReportProgress,
    setRecoveryNotice,
    showControls: () => setControlsVisible(true),
    showPlaybackPulse,
    closeMenu: () => setMenu(null),
    playItem: (itemId) => navigation.replace("Player", { itemId }),
    nextEpisodePromptEnabled: preferences.playerControls &&
      preferences.playerControlVisibility.nextEpisodePrompt !== false,
  });

  const generatedTrickplayQuery = usePlayerTrickplay({
    item,
    sources,
    selectedSourceId: source?.Id,
    serverUrl: credentials?.serverUrl,
    playing: lifecycle.status === "playing",
  });
  const {
    seek,
    selectSource,
    handlePlaybackError,
    retryPlayback,
    togglePaused,
    toggleMuted,
    adjustVolume,
    setVolume,
    suspend,
  } = runtimeActions;
  useEffect(() => setAppActivateHandler(!controlsVisible ? () => {
    togglePaused();
    setControlsVisible(true);
    return true;
  } : undefined), [controlsVisible, setControlsVisible, togglePaused]);
  usePlayerTVRemoteControls({
    enabled: televisionRuntime,
    controlsVisible,
    menuOpen: Boolean(menu),
    onRevealControls: () => {
      noteControlsActivity();
      requestAnimationFrame(() => requestAnimationFrame(() => focusRegisteredNode("player.play")));
    },
    onTogglePlayerControls: () => setControlsVisible((visible) => !visible),
    onTogglePlayback: togglePaused,
    onSeek: (direction) => seek(positionRef.current + direction * (direction > 0 ? preferences.seekForwardSeconds : preferences.seekBackSeconds)),
    onAdjustVolume: adjustVolume,
    onToggleMute: toggleMuted,
    onPreviousEpisode: () => previousEpisode && playEpisode(previousEpisode),
    onNextEpisode: () => nextEpisode && playEpisode(nextEpisode),
  });
  const playSessionId = playbackQuery.data?.PlaySessionId;
  const playbackRequestLogRef = useRef("");
  useEffect(() => {
    if (playbackQuery.fetchStatus !== "fetching") return;
    const key = `${route.params.itemId}:${selectedSourceId || "automatic"}`;
    if (playbackRequestLogRef.current === key) return;
    playbackRequestLogRef.current = key;
    console.info("[player] requesting playback info", {
      itemId: route.params.itemId,
      mediaSourceId: selectedSourceId || undefined,
    });
  }, [playbackQuery.fetchStatus, route.params.itemId, selectedSourceId]);
  useEffect(() => {
    if (!playbackQuery.data) return;
    console.info("[player] playback info ready", {
      itemId: route.params.itemId,
      mediaSources: playbackQuery.data.MediaSources?.length ?? 0,
      playSessionId: Boolean(playbackQuery.data.PlaySessionId),
    });
  }, [playbackQuery.data, route.params.itemId]);
  const telemetryKey = playSessionId ?? surfaceIdentity;
  const sendTelemetryOnce = useCallback((
    event: string,
    details?: Record<string, unknown>,
    dedupeKey = event,
  ) => {
    if (!credentials || !telemetryKey) return;
    const identity = `${telemetryKey}:${dedupeKey}`;
    if (telemetryEventsRef.current.has(identity)) return;
    telemetryEventsRef.current.add(identity);
    void reportPlaybackTelemetry(credentials, {
      playbackKey: telemetryKey,
      event,
      elapsedMs: Date.now() - startupStartedAtRef.current,
      itemId: route.params.itemId,
      itemName: item?.Name,
      seriesName: item?.SeriesName,
      sourceId: source?.Id,
      sourceName: source?.Name,
      deliveryClass: deliveryBadge.label,
      details: {
        platform: Platform.OS,
        // Keep bitrate attached to every startup/stall event so browser and
        // native reports can distinguish transport/decoder failures from
        // memory pressure on the same high-bitrate asset.
        sourceBitrateMbps: source?.Bitrate
          ? Math.round((source.Bitrate / 1_000_000) * 10) / 10
          : undefined,
        videoBitrateKbps: source?.MediaStreams?.find((stream) => stream.Type === "Video")?.BitRate,
        prewarmHit: isPrewarmLeaseUsable(playSessionId),
        sourceFingerprint: source
          ? getPlaybackSourceFingerprint(source)
          : undefined,
        hlsStartupVariant: getHlsStartupVariant(streamUrl),
        hlsSegmentLength: getHlsSegmentLength(playSessionId),
        ...details,
      },
    }).catch(() => undefined);
  }, [
    credentials,
    deliveryBadge.label,
    item?.Name,
    item?.SeriesName,
    playSessionId,
    route.params.itemId,
    source,
    streamUrl,
    telemetryKey,
  ]);
  useEffect(() => {
    if (playSessionId && source) {
      sendTelemetryOnce("client-playback-requested");
    }
  }, [playSessionId, sendTelemetryOnce, source]);
  useEffect(() => {
    if (lifecycle.status === "playing") {
      sendTelemetryOnce("client-first-frame", {
        positionSeconds: positionRef.current,
      });
    } else if (
      lifecycle.status === "buffering" &&
      playbackStartedRef.current &&
      !pausedRef.current
    ) {
      // Key by lifecycle generation so a continuous stall is one event while
      // a later independent stall remains observable.
      sendTelemetryOnce(
        "client-buffering",
        {
          generation: lifecycle.generation,
          positionSeconds: positionRef.current,
          bufferedSeconds: buffered,
        },
        `client-buffering-${lifecycle.generation}`,
      );
    }
  }, [
    buffered,
    lifecycle.generation,
    lifecycle.status,
    pausedRef,
    playbackStartedRef,
    positionRef,
    sendTelemetryOnce,
  ]);
  const poster =
    credentials && item?.Id
      ? imageSource(
          credentials,
          {
            ...item,
            Id: item.ParentBackdropItemId ?? item.SeriesId ?? item.Id,
          },
          item.ParentBackdropImageTags?.length || item.BackdropImageTags?.length
            ? "Backdrop"
            : "Primary",
          1920,
        )
      : undefined;
  const finishesAtLabel = duration > 1
    ? new Date(Date.now() + Math.max(0, duration - position) * 1000).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })
    : "";
  const videoStream = videoStreams.find((entry) => entry.Type === "Video");
  const activeAudioStream = audioStreams.find((entry) => entry.Index === selectedAudioIndex) ?? audioStreams.find((entry) => entry.IsDefault) ?? audioStreams[0];
  const activeSubtitleStream = subtitleStreams.find((entry) => entry.Index === selectedSubtitleIndex);
  // Build full subtitle track descriptors for the web surface (JASSUB/libbitsub/VTT).
  // Native mpv surfaces only use subtitleTrackIndex for embedded tracks.
  const subtitleTrackDescriptors = useMemo(
    () =>
      credentials && item?.Id && source?.Id
        ? buildSubtitleTracks(
            credentials,
            item.Id,
            source.Id,
            subtitleStreams,
            source.TranscodingUrl,
          )
        : [],
    [credentials, item?.Id, source?.Id, source?.TranscodingUrl, subtitleStreams],
  );
  const activeSubtitleRenderIndex =
    selectedSubtitleIndex == null
      ? null
      : subtitleStreams.length > 0
        ? subtitleStreams.findIndex(
            (entry) => entry.Index === selectedSubtitleIndex,
          )
        : null;
  const audioSubtitleSummary = `${activeAudioStream?.Language?.toUpperCase() || "Audio"} / ${activeSubtitleStream?.Language?.toUpperCase() || "Off"}`;
  const showAudioTrackList = preferences.playerControls && preferences.playerControlVisibility.audioTrackList !== false;
  const showSubtitleTrackList = preferences.playerControls && preferences.playerControlVisibility.subtitleTrackList !== false;
  const showPreferredAudio = preferences.playerControls && preferences.playerControlVisibility.preferredAudioLanguage !== false;
  const showPreferredSubtitles = preferences.playerControls && preferences.playerControlVisibility.preferredSubtitleLanguage !== false;
  const languageMenuEntries = useMemo<LanguageMenuEntry[]>(
    () => buildLanguageMenuEntries({
      audioStreams,
      subtitleStreams,
      showAudio: showAudioTrackList,
      showSubtitles: showSubtitleTrackList,
    }),
    [audioStreams, showAudioTrackList, showSubtitleTrackList, subtitleStreams],
  );
  const playbackStats = useMemo(
    () => buildPlaybackStats({
      source,
      sources,
      videoStream,
      activeAudioStream,
      position,
      buffered,
      duration: timelineDuration,
      playbackQuality: preferences.playbackQuality,
      hdrCapabilities: Platform.OS === "web"
        ? `${detectWebHdrCapabilities().display.toUpperCase()} (${formatHdrSourceLabel(videoStream?.VideoRangeType)})`
        : `Native display (${formatHdrSourceLabel(videoStream?.VideoRangeType)})`,
    }),
    [activeAudioStream, buffered, position, source, sources, timelineDuration, videoStream],
  );
  const diagnosticsMetrics = useMemo(
    () => buildDiagnosticsMetrics({
      section: diagnosticsSection,
      lifecycleStatus: lifecycle.status,
      generation: lifecycle.generation,
      surfaceIdentity,
      position,
      duration: timelineDuration,
      buffered,
      expectedRuntimeTicks: item?.RunTimeTicks,
      paused,
      seeking: timelineFocused,
      playbackStats,
      source,
      sources,
      deliveryLabel: deliveryBadge.label,
      capturedAt: new Date().toLocaleTimeString(),
    }),
    [buffered, deliveryBadge.label, diagnosticsSection, item?.RunTimeTicks, lifecycle.generation, lifecycle.status, paused, playbackStats, position, source, sources, surfaceIdentity, timelineDuration, timelineFocused],
  );
  const controlEnabled = (key: string) =>
    preferences.playerControls && preferences.playerControlVisibility[key] !== false;
  const handleSurfacePress = () => {
    if (menu) {
      setMenu(null);
      setControlsVisible(true);
      noteControlsActivity();
      return;
    }
    const now = Date.now();
    if (
      controlEnabled("doubleClickFullscreen") &&
      lastSurfacePressAt.current > 0 &&
      now - lastSurfacePressAt.current <= 350
    ) {
      // Second click — cancel the pending single-click pause and toggle fullscreen.
      if (clickTimerRef.current) { clearTimeout(clickTimerRef.current); clickTimerRef.current = null; }
      lastSurfacePressAt.current = 0;
      toggleFullscreen();
      setControlsFocused(false);
      setControlsVisible(true);
      return;
    }
    lastSurfacePressAt.current = now;
    if (!controlEnabled("clickToPlayPause")) {
      setControlsVisible(true);
      return;
    }
    setControlsVisible(true);
    // Defer single-click pause so a second click within 350ms can
    // cancel it and trigger fullscreen instead.
    if (clickTimerRef.current) clearTimeout(clickTimerRef.current);
    clickTimerRef.current = setTimeout(() => {
      clickTimerRef.current = null;
      togglePaused();
    }, 350);
  };

  const segmentControlEnabled = (type: MediaSegmentType) => type === "Intro"
    ? controlEnabled("skipIntro")
    : type === "Outro"
      ? controlEnabled("skipCredits")
      : preferences.playerControls;
  const promptSegment = segmentsQuery.data?.find((segment) => {
    const type = segment.Type as MediaSegmentType;
    return preferences.mediaSegmentActions[type] === "AskToSkip"
      && segmentControlEnabled(type)
      && segment.StartTicks !== undefined
      && segment.EndTicks !== undefined
      && position > (segment.StartTicks / 10_000_000) - 3
      && position < segment.EndTicks / 10_000_000;
  });
  const automaticSegment = segmentsQuery.data?.find((segment) => {
    const type = segment.Type as MediaSegmentType;
    return preferences.mediaSegmentActions[type] === "Skip"
      && segment.StartTicks !== undefined
      && segment.EndTicks !== undefined
      && position >= segment.StartTicks / 10_000_000
      && position < segment.EndTicks / 10_000_000;
  });
  const automaticSegmentKey = automaticSegment?.StartTicks !== undefined && automaticSegment?.EndTicks !== undefined
    ? `${route.params.itemId}:${automaticSegment.Type}:${automaticSegment.StartTicks}:${automaticSegment.EndTicks}`
    : "";
  const automaticSegmentEnd = automaticSegment?.EndTicks !== undefined ? automaticSegment.EndTicks / 10_000_000 : 0;
  useEffect(() => {
    automaticallySkippedSegmentsRef.current.clear();
  }, [route.params.itemId]);
  useEffect(() => {
    if (!automaticSegmentKey || !automaticSegmentEnd || lifecycle.status !== "playing") return;
    if (automaticallySkippedSegmentsRef.current.has(automaticSegmentKey)) return;
    automaticallySkippedSegmentsRef.current.add(automaticSegmentKey);
    seek(automaticSegmentEnd);
  }, [automaticSegmentEnd, automaticSegmentKey, lifecycle.status, seek]);
  const activeOutro = segmentsQuery.data?.find((segment) => segment.Type === "Outro" && segment.StartTicks !== undefined && segment.EndTicks !== undefined && position > (segment.StartTicks / 10_000_000) - 3 && position < segment.EndTicks / 10_000_000);
  const showNextPrompt = Boolean(
    nextEpisode &&
    preferences.autoplayNext &&
    preferences.showNextVideoInfoOverlay &&
    controlEnabled("nextEpisodePrompt") &&
    !nextPromptDismissed &&
    timelineDuration > 1 &&
    (timelineDuration - position <= 30 || position / timelineDuration >= 0.95),
  );
  const shouldPrefetchNextEpisode = Boolean(
    credentials &&
    nextEpisode?.Id &&
    lifecycle.status === "playing" &&
    (activeOutro || showNextPrompt),
  );
  useEffect(() => {
    if (!shouldPrefetchNextEpisode || !credentials || !nextEpisode?.Id) return;
    const nextItemId = nextEpisode.Id;
    void prefetchPlaybackInfoForItem(
      queryClient,
      credentials,
      nextItemId,
      preferences.audioLanguage,
    ).catch(() => undefined);
  }, [
    credentials,
    nextEpisode?.Id,
    preferences.audioLanguage,
    queryClient,
    shouldPrefetchNextEpisode,
  ]);

  usePlayerAppState(suspend, {
    shouldContinuePlayback: () => pictureInPictureRequestedRef.current,
    onActive: () => {
      pictureInPictureRequestedRef.current = false;
      setPictureInPicturePresentation(false);
    },
  });
  useEffect(() => {
    if (Platform.OS !== "android" || !pictureInPicturePresentation) return;
    const frame = requestAnimationFrame(() => {
      videoRef.current?.presentPictureInPicture();
    });
    const fallback = setTimeout(() => {
      if (AppState.currentState === "active") {
        pictureInPictureRequestedRef.current = false;
        setPictureInPicturePresentation(false);
      }
    }, 1_500);
    return () => {
      cancelAnimationFrame(frame);
      clearTimeout(fallback);
    };
  }, [pictureInPicturePresentation]);
  usePlayerStartupWatchdog({
    generationKey: surfaceIdentity,
    armed: Boolean(streamUrl) && !lifecycle.decoderReady && lifecycle.status !== "failed",
    // The native adapter validates managed child playlists before Media3 sees
    // them. Remux may hold that request for 15 seconds while a remote source
    // and hardware transcode open, so the browser-oriented deadline is too
    // short for a legitimate native startup.
    timeoutMs: Platform.OS === "web" ? 25_000 : 60_000,
    onTimeout: () => {
      // React may not have committed decoderReady yet even though the engine
      // already delivered playing/progress. A proven playing generation must
      // never be failed by an older startup deadline.
      if (playbackStartedRef.current) return;
      handlePlaybackError("Playback did not become ready in time.");
    },
  });
  const playEpisode = (episode: JellyfinItem) => {
    if (!episode.Id) return;
    navigation.replace("Player", { itemId: episode.Id });
  };
  if (!credentials) return null;
  // Series items are not playable — redirect to Item Details so the user
  // can pick an episode.
  if (item?.Type === "Series") {
    navigation.replace("Item", { itemId: item.Id! });
    return null;
  }
  const awaitingPlaybackInfo = Boolean(item && !playbackQuery.data && !playbackQuery.error);
  const loading = itemQuery.isLoading || awaitingPlaybackInfo || (!source && playbackQuery.isLoading);
  const preparingPlayback = lifecycle.status === "preparing";
  return (
    <SafeAreaView style={styles.safe} edges={[]}>
      <StatusBar hidden translucent backgroundColor="transparent" />
      <View style={styles.stage}>
        <PlayerKeyboardControls
          onActivity={noteControlsActivity}
          onTogglePlayback={togglePaused}
          onAdjustVolume={adjustVolume}
          onToggleMute={toggleMuted}
          onToggleFullscreen={toggleFullscreen}
          onDismiss={() => { setMenu(null); setControlsVisible(true); }}
        />
        <PlayerVideoSurface
          key={surfaceIdentity}
          ref={videoRef}
          uri={streamUrl}
          headers={{ "X-Emby-Token": credentials.accessToken }}
          posterSource={poster}
          paused={paused}
          muted={muted}
          volume={volume}
          rate={1}
          audioTrackIndex={audioTrackIndex !== undefined && audioTrackIndex >= 0 ? audioTrackIndex : undefined}
          subtitleTrackIndex={subtitleTrackIndex !== undefined && subtitleTrackIndex !== null && subtitleTrackIndex >= 0 ? subtitleTrackIndex : subtitleTrackIndex === null ? null : undefined}
          subtitleTracks={subtitleTrackDescriptors}
          activeSubtitleIndex={activeSubtitleRenderIndex !== undefined && activeSubtitleRenderIndex !== null && activeSubtitleRenderIndex >= 0 ? activeSubtitleRenderIndex : null}
          controlsVisible={controlsVisible}
          controlsHeight={useReferenceWindowDimensions().width >= 1024 ? 96 : 68}
          streamStartSeconds={streamStartSeconds}
          livePlayback={item?.Type === "TvChannel"}
          onTransportMetric={(metric) => {
            const loadSeconds = metric.loadMs / 1000;
            sendTelemetryOnce(
              "client-fragment-loaded",
              {
                bytes: metric.bytes,
                loadMs: metric.loadMs,
                mediaDurationSeconds: metric.mediaDurationSeconds,
                observedThroughputMbps: loadSeconds > 0
                  ? Math.round((metric.bytes * 8 / loadSeconds / 1_000_000) * 10) / 10
                  : undefined,
              },
              "client-fragment-loaded",
            );
          }}
          {...decoderHandlers}
        />
        <Pressable
          accessibilityLabel={televisionRuntime ? (paused ? "Play" : "Pause") : undefined}
          accessibilityRole={televisionRuntime ? "button" : undefined}
          // The full-screen surface is the TV play/pause target only while
          // the player chrome is hidden. Once controls are visible it must
          // leave native focus available to the transport and menu buttons.
          focusable={televisionRuntime && !controlsVisible}
          hasTVPreferredFocus={televisionRuntime && !controlsVisible}
          onPointerMove={noteControlsActivity}
          onTouchStart={noteControlsActivity}
          onPress={televisionRuntime ? () => {
            noteControlsActivity();
            togglePaused();
          } : handleSurfacePress}
          style={styles.surfacePressable}
        />

        {!pictureInPicturePresentation && paused && lifecycle.decoderReady && lifecycle.status !== "failed" && controlEnabled("pauseOverlay") ? (
          <PlayerPausedOverlay
            item={item}
            width={width}
            height={height}
            compact={compact}
            portraitCompact={portraitCompact}
            shortLandscape={shortLandscape}
          />
        ) : null}

        {!pictureInPicturePresentation && (
          loading || !source ? (
            <PlayerPreparingSurface label={itemQuery.isLoading ? "Loading title..." : "Finding a playable source..."} />
          ) : preparingPlayback || lifecycle.status === "buffering" || lifecycle.status === "recovering" ? (
            <PlayerBufferingMark
              label={lifecycle.status === "recovering" ? "Recovering playback" : "Buffering"}
              reduceMotion={preferences.reduceMotion}
              television={televisionRuntime}
              compact={compact}
            />
          ) : null
        )}
        {!pictureInPicturePresentation && pulse && controlEnabled("playbackPulse") ? (
          <PlayerPlaybackPulse
            kind={pulse}
            compact={compact}
            opacity={pulseOpacity}
            scale={pulseScale}
            iconScale={pulseIconScale}
          />
        ) : null}
        <PlayerFailurePanel
          visible={!pictureInPicturePresentation && (lifecycle.status === "failed" || Boolean(!source && playbackQuery.error)) && menu !== "versions"}
          reduceMotion={preferences.reduceMotion}
          compact={compact}
          message={lifecycle.error || (playbackQuery.error instanceof Error ? playbackQuery.error.message : "No playable version was returned.")}
          canTryDifferentVersion={sources.length > 1}
          onRetry={retryPlayback}
          onTryDifferentVersion={() => { setControlsVisible(true); setMenu("versions"); }}
          onGoBack={navigation.goBack}
        />
        {!pictureInPicturePresentation && !preferences.simplePlaybackRecovery &&
        (controlEnabled("sourceFallbackStatus") ||
          controlEnabled("automaticDecisionToasts")) ? (
          <PlayerRecoveryNotice
            message={recoveryNotice}
            label={controlEnabled("automaticDecisionToasts") ? "AUTOMATIC RECOVERY" : "SOURCE RECOVERY"}
            reduceMotion={preferences.reduceMotion}
            onDismiss={() => setRecoveryNotice("")}
          />
        ) : null}

        <PlayerNextEpisodePrompt
          visible={!pictureInPicturePresentation && showNextPrompt}
          controlsVisible={controlsVisible}
          reduceMotion={preferences.reduceMotion}
          compact={compact}
          image={nextEpisode?.Id ? imageSource(credentials, nextEpisode, "Primary", 560) : undefined}
          meta={nextEpisode?.ParentIndexNumber !== undefined && nextEpisode?.IndexNumber !== undefined ? `Season ${nextEpisode.ParentIndexNumber}: Ep. ${nextEpisode.IndexNumber}` : "Up next"}
          title={nextEpisode?.Name ?? "Next episode"}
          onPlay={() => nextEpisode && playEpisode(nextEpisode)}
          onDismiss={() => setNextPromptDismissed(true)}
        />
        <PlayerSkipActions
          visible={!pictureInPicturePresentation && !showNextPrompt && Boolean(promptSegment)}
          controlsVisible={controlsVisible}
          reduceMotion={preferences.reduceMotion}
          compact={compact}
          segmentType={promptSegment?.Type}
          onSkip={() => promptSegment?.EndTicks && seek(promptSegment.EndTicks / 10_000_000)}
        />

        {!pictureInPicturePresentation && controlsRendered && preferences.playerControls ? (
          <Animated.View
            testID="player-controls-overlay"
            pointerEvents="box-none"
            style={[styles.controlsOverlay, { opacity: controlsOpacity }]}
          >
            <PlayerTopBar
              width={width}
              safeTop={safeArea.top}
              showBack={controlEnabled("topBackButton")}
              showTitle={controlEnabled("topTitle")}
              showFinishTime={controlEnabled("topFinishTime")}
              title={item?.Name ?? "Jellyflix"}
              finishesAt={finishesAtLabel}
              onBack={() => {
                navigation.replace("Item", { itemId: String(item?.Id ?? route.params.itemId) });
              }}
              onFocusChange={handleControlsFocusChange}
              onActivity={noteControlsActivity}
            />

            <View style={[
              styles.bottomRegion,
              portraitCompact && styles.bottomRegionPortrait,
              shortLandscape && styles.bottomRegionShort,
              { paddingBottom: (shortLandscape ? 4 : portraitCompact ? 8 : 14) + safeArea.bottom },
            ]}>
              <Svg pointerEvents="none" width={width} height={260} style={styles.bottomGradient}>
                <Defs>
                  <LinearGradient id="player-bottom-fade" x1="0" y1="1" x2="0" y2="0">
                    <Stop offset="0" stopColor="#000" stopOpacity={0.92} />
                    <Stop offset="0.42" stopColor="#000" stopOpacity={0.58} />
                    <Stop offset="1" stopColor="#000" stopOpacity={0} />
                  </LinearGradient>
                </Defs>
                <Rect width={width} height={260} fill="url(#player-bottom-fade)" />
              </Svg>
              <View style={[styles.bottomControls, portraitCompact && styles.bottomControlsPortrait, shortLandscape && styles.bottomControlsShort]}>
              {controlEnabled("progressBar") || controlEnabled("duration") ? (
                <PlayerTimeline
                  credentials={credentials}
                  item={item}
                  sourceId={source?.Id}
                  generatedTrickplay={generatedTrickplayQuery.data}
                  preloadedTrickplayUrls={generatedTrickplayQuery.preloadedUrls}
                  segments={segmentsQuery.data ?? []}
                  position={position}
                  buffered={buffered}
                  duration={timelineDuration}
                  compact={compact}
                  shortLandscape={shortLandscape}
                  showProgress={controlEnabled("progressBar")}
                  showDuration={controlEnabled("duration")}
                  showTrickplay={preferences.playerControls && preferences.playerControlVisibility.trickplayPreview !== false}
                  seekBackSeconds={preferences.seekBackSeconds}
                  seekForwardSeconds={preferences.seekForwardSeconds}
                  showRemainingTime={preferences.showVideoRemainingTime}
                  getPosition={() => positionRef.current}
                  onSeek={seek}
                  onActivity={noteControlsActivity}
                  onFocusChange={(focused) => { setTimelineFocused(focused); handleControlsFocusChange(focused); }}
                />
              ) : null}
              <View style={[styles.controlRow, compact && styles.controlRowCompact, shortLandscape && styles.controlRowShort]}>
                <PlayerTransportControls
                  compact={compact}
                  shortLandscape={shortLandscape}
                  paused={paused}
                  muted={muted}
                  volume={volume}
                  position={position}
                  seekBackSeconds={preferences.seekBackSeconds}
                  seekForwardSeconds={preferences.seekForwardSeconds}
                  title={item?.SeriesName ? `${item.SeriesName} · ${item.Name ?? "Episode"}` : item?.Name ?? "Now playing"}
                  hasPrevious={Boolean(previousEpisode)}
                  hasNext={Boolean(nextEpisode)}
                  isEnabled={controlEnabled}
                  onTogglePaused={togglePaused}
                  onToggleMuted={toggleMuted}
                  onSeek={seek}
                  onPrevious={() => previousEpisode && playEpisode(previousEpisode)}
                  onNext={() => nextEpisode && playEpisode(nextEpisode)}
                  onAdjustVolume={adjustVolume}
                  onSetVolume={setVolume}
                  onFocusChange={handleControlsFocusChange}
                  onActivity={noteControlsActivity}
                />
                <PlayerMenuControls
                  compact={compact}
                  shortLandscape={shortLandscape}
                  menu={menu}
                  deliveryLabel={deliveryBadge.label}
                  deliveryAudioOnly={deliveryBadge.audioOnly}
                  deliveryTone={deliveryBadge.tone}
                  deliveryAccessibilityLabel={`${sources.length} playback versions, ${item?.MediaSources?.length ?? 0} item versions, ${playbackQuery.data?.MediaSources?.length ?? 0} probed versions`}
                  versionLabel={source ? getPlaybackSourceLabel(source, Math.max(0, sources.indexOf(source))) : playbackQuery.isLoading ? "Loading…" : "Unavailable"}
                  hasVersions={Boolean(source && sources.length > 0)}
                  hasEpisodes={Boolean(item?.SeriesId)}
                  hasLanguage={audioStreams.length > 0 || subtitleStreams.length > 0}
                  audioSubtitleSummary={audioStreams.length > 0 || subtitleStreams.length > 0 ? audioSubtitleSummary : playbackQuery.isLoading ? "Loading…" : "Unavailable"}
                  fullscreenActive={fullscreenActive}
                  isEnabled={controlEnabled}
                  onMenuChange={setMenu}
                  onPictureInPicture={() => {
                    if (Platform.OS === "android") {
                      pictureInPictureRequestedRef.current = true;
                      setPictureInPicturePresentation(true);
                      setMenu(null);
                      setControlsVisible(false);
                      return;
                    }
                    videoRef.current?.presentPictureInPicture();
                  }}
                  onFullscreen={() => {
                    setControlsFocused(false);
                    toggleFullscreen();
                  }}
                  onFocusChange={handleControlsFocusChange}
                  onActivity={noteControlsActivity}
                />
              </View>
              </View>
            </View>
          </Animated.View>
        ) : null}

        {!pictureInPicturePresentation && menu ? (
          <PlayerMenuShell
            menu={menu}
            width={width}
            height={height}
            compact={compact}
            shortLandscape={shortLandscape}
            reduceMotion={preferences.reduceMotion}
            onDismiss={() => {
              setMenu(null);
              setControlsVisible(true);
              noteControlsActivity();
            }}
          >
            {menu !== "versions" && menu !== "episodes" && menu !== "stats" ? <Text style={styles.menuTitle}>
              {menu === "options" ? "Playback Options" : menu === "language" ? "Language and Subtitles" : menu === "audio" ? "Audio" : "Subtitles"}
            </Text> : null}
            {menu === "episodes" ? (
              <PlayerEpisodesMenu
                credentials={credentials}
                currentItemId={item?.Id}
                seasons={seasons}
                episodes={episodes}
                selectedSeason={selectedSeason}
                selectedSeasonId={selectedSeasonId}
                television={televisionRuntime}
                shortLandscape={shortLandscape}
                seasonError={seasonQuery.isError}
                episodeError={episodeQuery.isError}
                episodeLoading={episodeQuery.isLoading}
                seasonFetching={seasonQuery.isFetching}
                episodeFetching={episodeQuery.isFetching}
                onSelectSeason={setSelectedSeasonId}
                onPlayEpisode={playEpisode}
                onClose={() => setMenu(null)}
                onRetrySeasons={() => { void seasonQuery.refetch(); }}
                onRetryEpisodes={() => { void episodeQuery.refetch(); }}
              />
            ) : menu === "versions" ? (
              <PlayerVersionsMenu
                entries={rankedSources}
                selectedSourceId={source?.Id}
                compact={compact}
                canPin={Boolean(item?.SeriesId)}
                onSelect={(entry) => selectSource(entry.source)}
                onTogglePinned={togglePinnedSource}
                onToggleIgnored={toggleIgnoredSource}
              />
            ) : menu === "stats" ? (
              <PlayerDiagnosticsMenu
                compact={compact}
                section={diagnosticsSection}
                metrics={diagnosticsMetrics}
                status={lifecycle.status}
                method={source?.TranscodingUrl ? "Transcode" : source?.SupportsDirectPlay ? "Direct" : "Stream"}
                sharePayload={{ itemId: item?.Id, lifecycle, source, metrics: diagnosticsMetrics }}
                onSectionChange={setDiagnosticsSection}
              />
            ) : menu === "options" ? (
              <PlayerOptionsMenu
                preferences={preferences}
                onToggle={(key) => {
                  setPreferences((current) => {
                    const next = { ...current, [key]: !current[key] };
                    void savePreferences(next);
                    return next;
                  });
                }}
                onSetQuality={(playbackQuality) => {
                  setPreferences((current) => {
                    const next = { ...current, playbackQuality };
                    void savePreferences(next);
                    return next;
                  });
                }}
                onClearScoring={() => {
                  clearSourceScoring();
                  setMenu(null);
                }}
              />
            ) : menu === "language" ? (
              <PlayerLanguageMenu
                entries={languageMenuEntries}
                selectedAudioIndex={selectedAudioIndex}
                selectedSubtitleIndex={selectedSubtitleIndex}
                showPreferredAudio={showPreferredAudio}
                showPreferredSubtitles={showPreferredSubtitles}
                preferredAudio={preferences.audioLanguage}
                preferredSubtitles={preferences.subtitleLanguage}
                onSelectAudio={selectAudioTrack}
                onSelectSubtitle={selectSubtitleTrack}
              />
            ) : (
              <PlayerTrackMenu
                mode={menu === "audio" ? "audio" : "subtitles"}
                streams={menu === "audio" ? audioStreams : subtitleStreams}
                selectedAudioIndex={selectedAudioIndex}
                selectedSubtitleIndex={selectedSubtitleIndex}
                onSelectAudio={selectAudioTrack}
                onSelectSubtitle={selectSubtitleTrack}
                onClose={() => setMenu(null)}
              />
            )}
          </PlayerMenuShell>
        ) : null}
      </View>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  safe: { flex: 1, backgroundColor: "#000" },
  stage: { flex: 1, backgroundColor: "#000" },
  surfacePressable: { ...StyleSheet.absoluteFillObject },
  controlsOverlay: { ...StyleSheet.absoluteFillObject, zIndex: 20, justifyContent: "space-between" },
  bottomRegion: { paddingHorizontal: 32, paddingBottom: 14, justifyContent: "flex-end" },
  bottomRegionPortrait: { paddingHorizontal: 10 },
  bottomRegionShort: { paddingHorizontal: 8, paddingBottom: 4 },
  bottomGradient: { position: "absolute", left: 0, bottom: 0 },
  bottomControls: {
    paddingHorizontal: 12,
    paddingTop: 10,
    paddingBottom: 11,
    borderWidth: 1,
    borderColor: "rgba(255,255,255,0.09)",
    borderRadius: 16,
    backgroundColor: "rgba(6,8,12,0.42)",
    shadowColor: "#000",
    shadowOpacity: 0.22,
    shadowRadius: 18,
    shadowOffset: { width: 0, height: 8 },
    elevation: 5,
  },
  bottomControlsPortrait: { paddingHorizontal: 8, paddingBottom: 16, borderRadius: 13 },
  bottomControlsShort: { paddingHorizontal: 7, paddingTop: 6, paddingBottom: 6, borderRadius: 12, backgroundColor: "rgba(6,8,12,0.36)" },
  controlRow: {
    minHeight: 42,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 16,
  },
  controlRowCompact: {
    minHeight: 84,
    flexDirection: "column",
    justifyContent: "center",
    gap: 4,
  },
  controlRowShort: { minHeight: 32, flexDirection: "row", flexWrap: "nowrap", gap: 4 },
  menuTitle: {
    marginBottom: 4,
    paddingHorizontal: 10,
    paddingTop: 7,
    paddingBottom: 8,
    color: "#fff",
    fontFamily: playerFont,
    fontSize: 15,
    lineHeight: 20,
    fontWeight: "900",
  },
});
